import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { useLocaleFormatters } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/publicApi';
import { ApiError } from '@wrapforce/shared/errors';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string;

export function DepositCard({ token, depositCents, shopName, onBeforePay }: { token: string; depositCents: number; shopName: string; onBeforePay?: () => Promise<void> }) {
  const { t } = useTranslation('publicQuote');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  // A coded 4xx from the API carries copy meant for the customer (quote no
  // longer payable, deposit already paid); anything else gets the generic
  // message. Keeping the raw error out of state lets the effect depend on the
  // token alone, so a translation reload does not refire the request.
  const [error, setError] = useState<{ message: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    publicApi
      .post<{ clientSecret: string; stripeAccountId: string }>(`/api/v1/public/quotes/${token}/pay-intent`)
      .then((r) => {
        if (cancelled) return;
        setClientSecret(r.clientSecret);
        setStripeAccountId(r.stripeAccountId);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError({ message: err instanceof ApiError && err.status < 500 ? err.message : null });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (error) return <div className="text-sm text-danger">{error.message ?? t('deposit.startError')}</div>;
  if (!clientSecret || !stripeAccountId) {
    return <div className="text-sm text-muted">{t('deposit.loadingForm')}</div>;
  }

  const stripePromise = loadStripe(stripePublishableKey, { stripeAccount: stripeAccountId });

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <PayForm token={token} depositCents={depositCents} shopName={shopName} onBeforePay={onBeforePay} />
    </Elements>
  );
}

function PayForm({ token, depositCents, shopName, onBeforePay }: { token: string; depositCents: number; shopName: string; onBeforePay?: () => Promise<void> }) {
  const { t } = useTranslation('publicQuote');
  const { formatMoney } = useLocaleFormatters();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    // Paying is how a deposit quote gets accepted, so the customer's booking
    // preference/details are persisted first; a failure here (e.g. closed-day
    // validation) blocks the payment rather than silently dropping the info.
    if (onBeforePay) {
      try {
        await onBeforePay();
      } catch {
        setError(t('deposit.preferenceSaveFailed'));
        setSubmitting(false);
        return;
      }
    }
    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/q/${token}?paid=1` },
    });
    if (confirmError) {
      setError(confirmError.message ?? t('deposit.paymentFailed'));
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-ink">{t('deposit.reserveSlot')}</div>
          <div className="text-xs text-muted">{t('deposit.depositAppliedToTotal')}</div>
        </div>
        <div className="font-mono text-lg font-bold">{formatMoney(depositCents)}</div>
      </div>
      <PaymentElement />
      {error ? <div className="text-sm text-danger">{error}</div> : null}
      <Button type="submit" loading={submitting} disabled={!stripe}>
        {t('deposit.payDeposit', { amount: formatMoney(depositCents) })}
      </Button>
      <div className="text-center text-[11px] text-muted">{t('deposit.securedByStripe', { shopName })}</div>
    </form>
  );
}
